import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const B2BAgentContext = createContext(null);

const TOKEN_KEY = 'b2b_agent_token';
const AGENT_KEY = 'b2b_agent_info';

export function B2BAgentProvider({ children }) {
    const [agent, setAgent]   = useState(() => {
        try { const s = localStorage.getItem(AGENT_KEY); return s ? JSON.parse(s) : null; } catch { return null; }
    });
    const [token, setToken]   = useState(() => localStorage.getItem(TOKEN_KEY) || null);
    const [loading, setLoading] = useState(false);

    // Check the session with the server — on mount, when the agent comes back
    // to the tab, and every 10 minutes. It used to be checked on mount only:
    // a session that expired (7 days) or an agent suspended while a tab was
    // open kept agent prices on screen, and bookings then went through with
    // no agent and no commission. Only a 401/403 logs out — never a network
    // blip or a server error.
    useEffect(() => {
        if (!token) return undefined;
        let last = 0;
        const verify = () => {
            if (Date.now() - last < 60 * 1000) return;
            last = Date.now();
            fetch('/api/b2b/auth/me', { headers: { 'x-b2b-token': token } })
                .then(async (r) => {
                    if (r.status === 401 || r.status === 403) {
                        setAgent(null);
                        setToken(null);
                        localStorage.removeItem(TOKEN_KEY);
                        localStorage.removeItem(AGENT_KEY);
                        return;
                    }
                    const d = await r.json().catch(() => null);
                    if (d && d.ok && d.agent) {
                        setAgent(d.agent);
                        localStorage.setItem(AGENT_KEY, JSON.stringify(d.agent));
                    }
                })
                .catch(() => {});
        };
        verify();
        const onVisible = () => { if (document.visibilityState === 'visible') verify(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);
        const timer = setInterval(verify, 10 * 60 * 1000);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
            clearInterval(timer);
        };
    }, [token]);

    const login = useCallback(async (email, password) => {
        setLoading(true);
        try {
            const res  = await fetch('/api/b2b/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });
            const data = await res.json();
            // `code` lets the page show the message in the visitor's language.
            if (!data.ok) return { ok: false, code: data.code || '', error: data.error || 'Login failed' };
            setToken(data.token);
            setAgent(data.agent);
            localStorage.setItem(TOKEN_KEY, data.token);
            localStorage.setItem(AGENT_KEY, JSON.stringify(data.agent));
            return { ok: true };
        } catch (err) {
            return { ok: false, code: 'network', error: err.message };
        } finally {
            setLoading(false);
        }
    }, []);

    const logout = useCallback(async () => {
        if (token) {
            fetch('/api/b2b/auth/logout', {
                method: 'POST',
                headers: { 'x-b2b-token': token },
            }).catch(() => {});
        }
        setAgent(null);
        setToken(null);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(AGENT_KEY);
    }, [token]);

    // Stable headers object — only recreated when the token actually changes
    const b2bHeaders = useMemo(() => (token ? { 'x-b2b-token': token } : {}), [token]);
    // Memoised: App re-renders on every navigation, and a fresh object here
    // re-rendered every consumer (memoised cards included) each time.
    const b2bValue = useMemo(() => ({ agent, token, loading, login, logout, b2bHeaders, isB2B: !!agent }),
        [agent, token, loading, login, logout, b2bHeaders]);

    return (
        <B2BAgentContext.Provider value={b2bValue}>
            {children}
        </B2BAgentContext.Provider>
    );
}

export const useB2BAgent = () => useContext(B2BAgentContext);

/** The logged-in agent's header for any booking call ({} when not an agent).
 *  The server attaches the agent and commission only when it is sent. */
export const readB2BHeaders = () => {
    try { const t = localStorage.getItem(TOKEN_KEY); return t ? { 'x-b2b-token': t } : {}; } catch { return {}; }
};
